/**
 * Deduplicate items by name: keep one per name, preferring higher-priority entries,
 * then tiebreaking by smaller id (string sort). Logs a console.warn when dropping a duplicate.
 */
export function deduplicateByName<T>(
  items: T[],
  options: {
    getName: (item: T) => string
    getId: (item: T) => string
    /** Return a numeric priority for the item. Higher values win during dedup. */
    getPriority: (item: T) => number
    /** Label for id in warnings (e.g. "id", "eskId"). Default "id". */
    idLabel?: string
    /** Optional prefix for log messages (e.g. "[ESK] "). */
    logPrefix?: string
  },
): T[] {
  const { getName, getId, getPriority, idLabel = 'id', logPrefix = '' } = options
  const nameToWinner = new Map<string, T>()
  for (const item of items) {
    const name = getName(item)
    const existing = nameToWinner.get(name)
    if (!existing) {
      nameToWinner.set(name, item)
    } else {
      const existingPriority = getPriority(existing)
      const currentPriority = getPriority(item)
      let keepCurrent: boolean
      if (currentPriority > existingPriority) {
        keepCurrent = true
      } else if (currentPriority < existingPriority) {
        keepCurrent = false
      } else {
        const existingId = getId(existing)
        const currentId = getId(item)
        keepCurrent = currentId.localeCompare(existingId) < 0
      }
      console.warn(
        `${logPrefix}Duplicate sidekick name "${name}" (${idLabel}: ${keepCurrent ? getId(existing) : getId(item)}). Keeping entry with priority ${keepCurrent ? currentPriority : existingPriority} over ${keepCurrent ? existingPriority : currentPriority}.`,
      )
      if (keepCurrent) {
        nameToWinner.set(name, item)
      }
    }
  }
  return items.filter(item => nameToWinner.get(getName(item)) === item)
}
